import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { B, FONT } from '../lib/constants';

export default function ChecklistTab({ project, isBuilder = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draftLists, setDraftLists] = useState([]);      // titled lists with no items yet
  const [creatingList, setCreatingList] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');
  const [addingTo, setAddingTo] = useState(null);        // list name currently adding an item to
  const [renaming, setRenaming] = useState(null);        // list name being renamed
  const [renameText, setRenameText] = useState('');
  const [itemText, setItemText] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await supabase.from('checklist_items').select('*')
      .eq('project_id', project.id)
      .order('done', { ascending: true })
      .order('created_at', { ascending: false });
    if (data) setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [project.id]);

  const savedListNames = [...new Set(items.map(i => i.list_name))];
  // Clients always see "Dan's To-Do List" as an available checklist
  const defaultLists = isBuilder ? [] : ["Dan's To-Do List"];
  const allListNames = [...new Set([...defaultLists, ...savedListNames, ...draftLists])];

  const handleCreateList = () => {
    const title = newListTitle.trim();
    if (!title) return;
    if (!allListNames.includes(title)) setDraftLists(prev => [...prev, title]);
    setNewListTitle('');
    setCreatingList(false);
    setAddingTo(title); // jump straight into adding the first item
  };

  const handleAddItem = async (listName) => {
    if (!itemText.trim()) return;
    setSaving(true);
    await supabase.from('checklist_items').insert({
      project_id: parseInt(project.id),
      list_name: listName,
      item: itemText.trim(),
      created_by: isBuilder ? 'builder' : 'client',
      done: false,
    });
    if (!isBuilder) {
      try {
        const { notifyBuilder } = await import('../hooks/usePushNotifications');
        await notifyBuilder(`☑️ Checklist Item Added — ${project.name}`, `${listName}: ${itemText.trim().slice(0, 80)}`, `${window.location.origin}?project=${project.id}&tab=checklist`);
      } catch(e) {}
    }
    setItemText('');
    setDraftLists(prev => prev.filter(n => n !== listName)); // list is real now
    setSaving(false);
    load();
  };

  const handleToggle = async (it) => {
    const nowDone = !it.done;
    await supabase.from('checklist_items').update({
      done: nowDone,
      done_at: nowDone ? new Date().toISOString() : null,
      done_by: nowDone ? (isBuilder ? 'builder' : 'client') : null,
    }).eq('id', it.id);
    if (nowDone) {
      try {
        if (!isBuilder) {
          const { notifyBuilder } = await import('../hooks/usePushNotifications');
          await notifyBuilder(`✓ Checklist Item Done — ${project.name}`, `${it.list_name}: ${it.item.slice(0, 80)}`, `${window.location.origin}?project=${project.id}&tab=checklist`);
        } else {
          const { notifyProjectClients } = await import('../hooks/usePushNotifications');
          await notifyProjectClients(project.id, `✓ Completed — ${it.list_name}`, it.item.slice(0, 80), `${window.location.origin}?project=${project.id}&tab=checklist`);
        }
      } catch(e) {}
    }
    load();
  };

  const handleDelete = async (it) => {
    await supabase.from('checklist_items').delete().eq('id', it.id);
    load();
  };

  const handleRename = async (oldName) => {
    const newName = renameText.trim();
    if (!newName || newName === oldName) { setRenaming(null); return; }
    await supabase.from('checklist_items')
      .update({ list_name: newName })
      .eq('project_id', project.id)
      .eq('list_name', oldName);
    setDraftLists(prev => prev.map(n => n === oldName ? newName : n));
    setRenaming(null);
    setRenameText('');
    load();
  };

  const [taskedIds, setTaskedIds] = useState([]);
  const addAsTask = async (it) => {
    await supabase.from('todos').insert({
      project_id: parseInt(project.id),
      text: `${it.list_name}: ${it.item}`,
      priority: 'medium',
      source_checklist_id: it.id,
    });
    setTaskedIds(prev => [...prev, it.id]);
  };

  const inputStyle = { flex: 1, background: B.mid, border: `1px solid ${B.gold}`, borderRadius: 9, padding: '10px 12px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ color: B.white, fontSize: 18, fontWeight: 700 }}>Checklists</div>
        <button onClick={() => { setCreatingList(!creatingList); setNewListTitle(''); }}
          style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 9, padding: '8px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 800, fontFamily: FONT }}>
          {creatingList ? '× Cancel' : '+ New Checklist'}
        </button>
      </div>

      {creatingList && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <input value={newListTitle} onChange={e => setNewListTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreateList(); }}
            placeholder="Checklist title — e.g. Electrician, Final Walkthrough"
            autoFocus style={inputStyle} />
          <button onClick={handleCreateList} disabled={!newListTitle.trim()}
            style={{ background: newListTitle.trim() ? B.goldlt : B.slate, color: newListTitle.trim() ? B.black : B.muted, border: 'none', borderRadius: 9, padding: '10px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 800, fontFamily: FONT }}>
            Create
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ color: B.muted, textAlign: 'center', padding: 32 }}>Loading…</div>
      ) : allListNames.length === 0 ? (
        <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}` }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>☑️</div>
          <div style={{ color: B.muted, fontSize: 13, lineHeight: 1.6 }}>No checklists yet.<br/>Create one for a trade, a room, or a walkthrough.</div>
        </div>
      ) : allListNames.map(name => {
        const listItems = items.filter(i => i.list_name === name);
        const doneCount = listItems.filter(i => i.done).length;
        const isAdding = addingTo === name;
        return (
          <div key={name} style={{ background: B.navy, borderRadius: 14, padding: '16px 16px 12px', marginBottom: 14, border: `1px solid ${B.slate}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: listItems.length > 0 || isAdding ? 12 : 4, gap: 8 }}>
              {renaming === name ? (
                <div style={{ display: 'flex', gap: 6, flex: 1 }}>
                  <input value={renameText} onChange={e => setRenameText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleRename(name); if (e.key === 'Escape') setRenaming(null); }}
                    autoFocus
                    style={{ flex: 1, background: B.mid, border: `1px solid ${B.gold}`, borderRadius: 8, padding: '7px 10px', color: B.goldlt, fontSize: 13, fontWeight: 800, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', minWidth: 0 }} />
                  <button onClick={() => handleRename(name)}
                    style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 800, fontFamily: FONT }}>Save</button>
                  <button onClick={() => setRenaming(null)}
                    style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 8, padding: '7px 10px', cursor: 'pointer', color: B.muted, fontSize: 12, fontFamily: FONT }}>×</button>
                </div>
              ) : (
                <div onClick={() => { setRenaming(name); setRenameText(name); }}
                  style={{ color: B.goldlt, fontSize: 14, fontWeight: 800, cursor: 'pointer' }}
                  title="Tap to rename">
                  {name} <span style={{ color: '#555', fontSize: 11, fontWeight: 400 }}>✎</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {listItems.length > 0 && (
                  <div style={{ color: doneCount === listItems.length ? '#4CAF7D' : B.muted, fontSize: 11, fontWeight: 700 }}>
                    {doneCount}/{listItems.length}
                  </div>
                )}
                <button onClick={() => { setAddingTo(isAdding ? null : name); setItemText(''); }}
                  style={{ background: 'transparent', border: `1px solid ${B.gold}`, borderRadius: 7, width: 26, height: 26, cursor: 'pointer', color: B.goldlt, fontSize: 15, fontWeight: 700, fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isAdding ? '×' : '+'}
                </button>
              </div>
            </div>

            {isAdding && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input value={itemText} onChange={e => setItemText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddItem(name); }}
                  placeholder="Add an item…" autoFocus style={inputStyle} />
                <button onClick={() => handleAddItem(name)} disabled={saving || !itemText.trim()}
                  style={{ background: itemText.trim() ? B.goldlt : B.slate, color: itemText.trim() ? B.black : B.muted, border: 'none', borderRadius: 9, padding: '10px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 800, fontFamily: FONT }}>
                  {saving ? '…' : 'Add'}
                </button>
              </div>
            )}

            {listItems.length === 0 && !isAdding && (
              <div style={{ color: '#555', fontSize: 12, paddingBottom: 4 }}>Empty — tap + to add items.</div>
            )}

            {listItems.map(it => (
              <div key={it.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: '#0F0F0F', borderRadius: 10, padding: '11px 13px', marginBottom: 6, border: `1px solid ${it.done ? '#1E2A22' : '#1E1E24'}`, opacity: it.done ? 0.6 : 1 }}>
                <button onClick={() => handleToggle(it)}
                  style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${it.done ? '#4CAF7D' : B.gold}`, background: it.done ? '#4CAF7D' : 'transparent', cursor: 'pointer', flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {it.done && <span style={{ color: '#fff', fontSize: 13, fontWeight: 800 }}>✓</span>}
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: it.done ? B.muted : B.white, fontSize: 13, lineHeight: 1.5, textDecoration: it.done ? 'line-through' : 'none' }}>{it.item}</div>
                  <div style={{ color: '#555', fontSize: 9, marginTop: 3 }}>
                    <span style={{ textTransform: 'capitalize' }}>Added by {it.created_by}</span>
                    {it.created_at ? ` · ${new Date(it.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${new Date(it.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}
                    {it.done && it.done_by ? <span style={{ textTransform: 'capitalize' }}>{` · Completed by ${it.done_by}`}</span> : ''}
                    {it.done && it.done_at ? ` ${new Date(it.done_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${new Date(it.done_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}
                  </div>
                </div>
                {isBuilder && !it.done && (
                  taskedIds.includes(it.id) ? (
                    <span style={{ color: '#4CAF7D', fontSize: 10, fontWeight: 700, flexShrink: 0, alignSelf: 'center' }}>✓ Tasked</span>
                  ) : (
                    <button onClick={() => addAsTask(it)}
                      style={{ background: 'rgba(248,218,107,0.08)', border: `1px solid ${B.gold}`, borderRadius: 7, padding: '4px 9px', cursor: 'pointer', color: B.goldlt, fontSize: 10, fontWeight: 700, fontFamily: FONT, flexShrink: 0, alignSelf: 'center', whiteSpace: 'nowrap' }}>
                      → Task
                    </button>
                  )
                )}
                <button onClick={() => handleDelete(it)}
                  style={{ background: 'none', border: 'none', color: '#444', fontSize: 16, cursor: 'pointer', padding: '0 2px', lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
